import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Search, Loader2, IdCard, Printer } from "lucide-react";
import { STATI_AFFIL, statoAffilBadge } from "@/components/accessi/utils";

export const Affiliazioni = ({ gruppi, annoCorrente, eventi = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [anno, setAnno] = useState("");
  const [stato, setStato] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importRes, setImportRes] = useState(null);
  const [importing, setImporting] = useState(false);

  // Stampa tesserini multipli
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchModo, setBatchModo] = useState("gruppo");
  const [batchAnno, setBatchAnno] = useState(annoCorrente);
  const [batchGruppo, setBatchGruppo] = useState("tutti");
  const [batchEvento, setBatchEvento] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);

  const stampaBatch = async () => {
    setBatchBusy(true);
    try {
      const params = { modo: batchModo };
      if (batchModo === "evento") {
        if (!batchEvento) { toast.error("Seleziona un evento"); setBatchBusy(false); return; }
        params.evento_id = batchEvento;
      } else {
        params.anno = batchAnno || annoCorrente;
        if (batchGruppo !== "tutti") params.gruppo = batchGruppo;
      }
      const res = await api.get("/accessi/tesserini", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      setBatchOpen(false);
    } catch (e) { toast.error(extractError(e, "Nessun tesserino da stampare")); } finally { setBatchBusy(false); }
  };

  // Add dialog person picker
  const empty = { person_id: "", anno: annoCorrente, stato: "attiva", gruppo: "", numero_tessera: "", codice_ente_affiliazione: "", data_affiliazione: "", note: "" };
  const [f, setF] = useState(empty);
  const [personQ, setPersonQ] = useState("");
  const [personResults, setPersonResults] = useState([]);
  const [personLabel, setPersonLabel] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (anno) params.anno = anno;
      if (stato !== "tutti") params.stato = stato;
      const { data } = await api.get("/accessi/affiliazioni", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  }, [q, anno, stato]);
  useEffect(() => { load(); }, [load]);

  const searchPersons = async (val) => {
    setPersonQ(val);
    if (val.trim().length < 2) { setPersonResults([]); return; }
    try { const { data } = await api.get("/persons", { params: { q: val, limit: 8 } }); setPersonResults(data.items || []); }
    catch (e) { console.error(e); }
  };

  const openNew = () => {
    setEdit(null); setF({ ...empty, anno: annoCorrente }); setPersonQ(""); setPersonResults([]); setPersonLabel(""); setOpen(true);
  };
  const openEdit = (a) => {
    setEdit(a); setF({ ...empty, ...a, anno: a.anno }); setPersonLabel(a.persona_nome || ""); setPersonResults([]); setOpen(true);
  };

  const save = async () => {
    if (!edit && !f.person_id) { toast.error("Seleziona una persona"); return; }
    setSaving(true);
    const payload = {
      anno: Number(f.anno) || annoCorrente, stato: f.stato, gruppo: f.gruppo || null,
      numero_tessera: f.numero_tessera || null, codice_ente_affiliazione: f.codice_ente_affiliazione || null,
      data_affiliazione: f.data_affiliazione || null, note: f.note || null,
    };
    try {
      if (edit) await api.put(`/accessi/affiliazioni/${edit.id}`, payload);
      else await api.post("/accessi/affiliazioni", { person_id: f.person_id, ...payload });
      toast.success("Affiliazione salvata"); setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm("Eliminare questa affiliazione?")) return;
    try { await api.delete(`/accessi/affiliazioni/${a.id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const stampaTesserino = async (a) => {
    try {
      const res = await api.get("/accessi/tesserino", { params: { person_id: a.person_id, anno: a.anno }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (e) { toast.error(extractError(e, "Errore nella stampa del tesserino")); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true); setImportRes(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/accessi/affiliazioni/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportRes(data); toast.success(`Import completato: ${data.creati} create, ${data.aggiornati} aggiornate`); load();
    } catch (e) { toast.error(extractError(e)); } finally { setImporting(false); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o codice fiscale" className="pl-9" data-testid="affil-search" />
        </div>
        <div className="w-28"><Label className="text-xs">Anno</Label><Input type="number" value={anno} onChange={(e) => setAnno(e.target.value)} placeholder="Tutti" data-testid="affil-anno-filter" /></div>
        <div className="w-40">
          <Label className="text-xs">Stato</Label>
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger data-testid="affil-stato-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {STATI_AFFIL.map((s) => <SelectItem key={s} value={s}>{statoAffilBadge(s).label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBatchOpen(true)} data-testid="affil-stampa-tesserini-btn"><Printer className="h-4 w-4 mr-2" /> Tesserini</Button>
          <Button variant="outline" onClick={() => { setImportOpen(true); setImportRes(null); }} data-testid="affil-import-btn"><Upload className="h-4 w-4 mr-2" /> Importa</Button>
          <Button onClick={openNew} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="affil-nuova-btn"><Plus className="h-4 w-4 mr-2" /> Nuova</Button>
        </div>
      </div>

      {loading ? <LoadingState /> : (items.length === 0 ? (
        <EmptyState title="Nessuna affiliazione" description="Aggiungi un'affiliazione o importa da Excel/CSV." />
      ) : (
        <Card className="divide-y overflow-hidden" data-testid="affil-list">
          {items.map((a) => {
            const b = statoAffilBadge(a.stato);
            return (
              <div key={a.id} className="p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.persona_nome || "-"} <span className="text-xs text-muted-foreground">· {a.anno}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{a.persona_cf || "CF n/d"}{a.gruppo ? ` · ${a.gruppo}` : ""}{a.numero_tessera ? ` · tessera ${a.numero_tessera}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                  <Button size="sm" variant="outline" onClick={() => stampaTesserino(a)} title="Stampa tesserino" data-testid={`affil-tesserino-${a.id}`}><IdCard className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`affil-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(a)} data-testid={`affil-del-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </Card>
      ))}

      {/* Dialog crea/modifica */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="affil-dialog">
          <DialogHeader><DialogTitle>{edit ? "Modifica affiliazione" : "Nuova affiliazione"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!edit && (
              <div className="sm:col-span-2">
                <Label>Persona *</Label>
                {personLabel ? (
                  <div className="flex items-center justify-between rounded-lg border p-2 bg-slate-50">
                    <span className="text-sm font-medium flex items-center gap-2"><IdCard className="h-4 w-4" /> {personLabel}</span>
                    <Button size="sm" variant="ghost" onClick={() => { setPersonLabel(""); setF({ ...f, person_id: "" }); }}>Cambia</Button>
                  </div>
                ) : (
                  <div>
                    <Input value={personQ} onChange={(e) => searchPersons(e.target.value)} placeholder="Cerca persona per nome o CF" data-testid="affil-person-search" />
                    {personResults.length > 0 && (
                      <div className="mt-1 border rounded-lg divide-y max-h-44 overflow-auto">
                        {personResults.map((p) => (
                          <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                            onClick={() => { setF({ ...f, person_id: p.id }); setPersonLabel(`${p.cognome || ""} ${p.nome || ""} ${p.codice_fiscale ? "· " + p.codice_fiscale : ""}`.trim()); setPersonResults([]); }}
                            data-testid={`affil-person-opt-${p.id}`}>
                            {(p.cognome || "")} {(p.nome || "")} <span className="text-xs text-muted-foreground">{p.codice_fiscale || ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div><Label>Anno *</Label><Input type="number" value={f.anno || ""} onChange={(e) => setF({ ...f, anno: e.target.value })} data-testid="affil-anno" /></div>
            <div>
              <Label>Stato *</Label>
              <Select value={f.stato} onValueChange={(v) => setF({ ...f, stato: v })}>
                <SelectTrigger data-testid="affil-stato"><SelectValue /></SelectTrigger>
                <SelectContent>{STATI_AFFIL.map((s) => <SelectItem key={s} value={s}>{statoAffilBadge(s).label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scuola / Gruppo</Label>
              <Select value={f.gruppo || undefined} onValueChange={(v) => setF({ ...f, gruppo: v })}>
                <SelectTrigger data-testid="affil-gruppo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{gruppi.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>N. tessera</Label><Input value={f.numero_tessera || ""} onChange={(e) => setF({ ...f, numero_tessera: e.target.value })} data-testid="affil-tessera" /></div>
            <div><Label>Codice ente affiliazione</Label><Input value={f.codice_ente_affiliazione || ""} onChange={(e) => setF({ ...f, codice_ente_affiliazione: e.target.value })} data-testid="affil-codice-ente" /></div>
            <div><Label>Data affiliazione</Label><Input type="date" value={(f.data_affiliazione || "").slice(0, 10)} onChange={(e) => setF({ ...f, data_affiliazione: e.target.value })} data-testid="affil-data" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Textarea value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="affil-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="affil-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md" data-testid="affil-import-dialog">
          <DialogHeader><DialogTitle>Importa affiliazioni</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Carica un file CSV o XLSX con le colonne: <b>codice_fiscale, anno, stato, gruppo, numero_tessera</b>. Le persone sono abbinate tramite codice fiscale.</p>
          <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => doImport(e.target.files?.[0])} disabled={importing} data-testid="affil-import-file" />
          {importing && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Importazione in corso…</div>}
          {importRes && (
            <div className="text-sm rounded-lg border p-3 bg-slate-50 space-y-1" data-testid="affil-import-result">
              <div>Create: <b>{importRes.creati}</b> · Aggiornate: <b>{importRes.aggiornati}</b></div>
              <div>Righe totali: {importRes.totale_righe}</div>
              {importRes.non_trovati?.length > 0 && <div className="text-[color:var(--brand-red-600)]">CF non trovati: {importRes.non_trovati.length}</div>}
              {importRes.errori?.length > 0 && <div className="text-[color:var(--brand-orange-600)]">Errori: {importRes.errori.length}</div>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog stampa tesserini multipli */}
      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-md" data-testid="affil-batch-dialog">
          <DialogHeader><DialogTitle>Stampa tesserini</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Genera un unico PDF con tutti i tesserini (con barcode e QR) pronti da ritagliare.</p>
          <div className="flex gap-2">
            <Button variant={batchModo === "gruppo" ? "default" : "outline"} size="sm" onClick={() => setBatchModo("gruppo")} className={batchModo === "gruppo" ? "bg-[color:var(--brand-blue-600)] text-white" : ""} data-testid="affil-batch-modo-gruppo">Per gruppo</Button>
            <Button variant={batchModo === "evento" ? "default" : "outline"} size="sm" onClick={() => setBatchModo("evento")} className={batchModo === "evento" ? "bg-[color:var(--brand-blue-600)] text-white" : ""} data-testid="affil-batch-modo-evento">Per evento</Button>
          </div>
          {batchModo === "gruppo" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Anno</Label><Input type="number" value={batchAnno || ""} onChange={(e) => setBatchAnno(e.target.value)} data-testid="affil-batch-anno" /></div>
              <div><Label>Gruppo</Label>
                <Select value={batchGruppo} onValueChange={setBatchGruppo}>
                  <SelectTrigger data-testid="affil-batch-gruppo"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="tutti">Tutti i gruppi</SelectItem>{gruppi.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div><Label>Evento</Label>
              <Select value={batchEvento || undefined} onValueChange={setBatchEvento}>
                <SelectTrigger data-testid="affil-batch-evento"><SelectValue placeholder="Seleziona evento" /></SelectTrigger>
                <SelectContent>{eventi.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome_evento}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchOpen(false)}>Annulla</Button>
            <Button onClick={stampaBatch} disabled={batchBusy} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="affil-batch-genera">
              {batchBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />} Genera PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
